const SELECT_MAX_WIDTH_PX = 600;
const SELECT_EXTRA_CHROME_PX = 88;

const SELECT_TAGS = new Set([
  'CBAR-SELECT',
  'CBAR-SELECT2',
  'CBAR-ENTITY-SELECT',
  'CBAR-MULTI-SELECT-COMBO-BOX',
]);

type SelectItem = {id?: string; title?: string; label?: string; value?: string};

type SizedSelectElement = HTMLElement & {
  items?: SelectItem[];
  label?: string;
};

let measureCanvas: HTMLCanvasElement | null = null;

function measureTextWidth(text: string, referenceEl: HTMLElement): number {
  if (!text) {
    return 0;
  }

  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas');
  }

  const context = measureCanvas.getContext('2d');
  if (!context) {
    return text.length * 8;
  }

  const styles = getComputedStyle(referenceEl);
  context.font = `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
  return context.measureText(text).width;
}

function parseItemsAttribute(element: HTMLElement): SelectItem[] {
  const raw = element.getAttribute('items');
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed as SelectItem[] : [];
  } catch {
    return [];
  }
}

function collectSelectLabels(element: SizedSelectElement): string[] {
  const labels = new Set<string>();

  const floatingLabel = element.getAttribute('label') || element.label;
  if (floatingLabel) {
    labels.add(floatingLabel);
  }

  const items = Array.isArray(element.items) && element.items.length
    ? element.items
    : parseItemsAttribute(element);

  items.forEach((item) => {
    const text = item.title || item.label || item.value || '';
    if (text) {
      labels.add(String(text));
    }
  });

  element.querySelectorAll('md-select-option').forEach((option) => {
    const text = option.textContent?.trim();
    if (text) {
      labels.add(text);
    }
  });

  return Array.from(labels);
}

function collectNativeSelectLabels(select: HTMLSelectElement): string[] {
  const labels = new Set<string>();
  Array.from(select.options).forEach((option) => {
    const text = option.textContent?.trim();
    if (text) {
      labels.add(text);
    }
  });
  return Array.from(labels);
}

function applySelectWidth(element: HTMLElement, widthPx: number): void {
  const width = `${Math.min(SELECT_MAX_WIDTH_PX, Math.max(0, widthPx))}px`;
  element.style.setProperty('--cbar-select-auto-width', width);
  element.style.setProperty('--cbar-select-width', width);
  element.style.maxWidth = `${SELECT_MAX_WIDTH_PX}px`;
}

function shouldUseFullWidthSelect(element: Element): boolean {
  const grid = element.closest('cbar-form-grid');
  if (!grid) {
    return false;
  }
  if (grid.hasAttribute('inline')) {
    return false;
  }
  if (grid.classList.contains('cbar-form-grid--columns')) {
    return false;
  }
  if (isFilterFormSelect(element)) {
    return false;
  }
  return true;
}

function sizeCbarSelectElement(element: SizedSelectElement): void {
  if (shouldUseFullWidthSelect(element)) {
    element.style.setProperty('--cbar-select-auto-width', '100%');
    element.style.setProperty('--cbar-select-width', '100%');
    element.style.width = '100%';
    element.style.maxWidth = '100%';

    const field = element.closest('.cbar-form-field');
    if (field instanceof HTMLElement) {
      field.style.width = '100%';
      field.style.maxWidth = '100%';
    }
    return;
  }

  const labels = collectSelectLabels(element);
  if (!labels.length) {
    return;
  }

  let maxLabelWidth = 0;
  labels.forEach((label) => {
    maxLabelWidth = Math.max(maxLabelWidth, measureTextWidth(label, element));
  });

  applySelectWidth(element, Math.ceil(maxLabelWidth + SELECT_EXTRA_CHROME_PX));

  const field = element.closest('.cbar-form-field');
  if (field instanceof HTMLElement && field.parentElement?.tagName === 'CBAR-FORM-GRID') {
    field.style.width = 'fit-content';
    field.style.maxWidth = '100%';
  }
}

function sizeNativeSelectElement(select: HTMLSelectElement): void {
  const labels = collectNativeSelectLabels(select);
  if (!labels.length) {
    return;
  }

  let maxLabelWidth = 0;
  labels.forEach((label) => {
    maxLabelWidth = Math.max(maxLabelWidth, measureTextWidth(label, select));
  });

  const width = Math.min(
    SELECT_MAX_WIDTH_PX,
    Math.ceil(maxLabelWidth + SELECT_EXTRA_CHROME_PX),
  );
  select.style.width = `${width}px`;
  select.style.maxWidth = `${SELECT_MAX_WIDTH_PX}px`;
}

function isSizedCbarSelect(element: Element): element is SizedSelectElement {
  return SELECT_TAGS.has(element.tagName);
}

function isFilterFormSelect(element: Element): boolean {
  return !!element.closest('form[name="filter_form"], form.cbar-filter-form');
}

/** Size dropdowns to their longest option label (capped at 600px). */
export function sizeFormSelects(root: ParentNode = document): void {
  const selects = new Set<Element>();

  root.querySelectorAll('cbar-form-grid cbar-select, cbar-form-grid cbar-select2, cbar-form-grid cbar-entity-select, cbar-form-grid cbar-multi-select-combo-box')
    .forEach((element) => selects.add(element));

  root.querySelectorAll('cbar-form-grid .cbar-form-field cbar-select, cbar-form-grid .cbar-form-field cbar-select2, cbar-form-grid .cbar-form-field cbar-entity-select, cbar-form-grid .cbar-form-field cbar-multi-select-combo-box')
    .forEach((element) => selects.add(element));

  selects.forEach((element) => {
    if (isFilterFormSelect(element)) {
      return;
    }
    if (isSizedCbarSelect(element)) {
      sizeCbarSelectElement(element);
    }
  });

  root.querySelectorAll('cbar-datatable select.filter-select')
    .forEach((element) => sizeNativeSelectElement(element as HTMLSelectElement));
}

function observeSelect(element: SizedSelectElement): void {
  if (element.dataset.cbarSelectSized === 'true') {
    sizeCbarSelectElement(element);
    return;
  }

  element.dataset.cbarSelectSized = 'true';
  const refresh = () => sizeCbarSelectElement(element);
  refresh();

  new MutationObserver(refresh).observe(element, {
    attributes: true,
    attributeFilter: ['items', 'value', 'label'],
    childList: true,
    subtree: true,
  });
}

function bindSelectObservers(root: ParentNode = document): void {
  root.querySelectorAll('cbar-form-grid cbar-select, cbar-form-grid cbar-select2, cbar-form-grid cbar-entity-select, cbar-form-grid cbar-multi-select-combo-box')
    .forEach((element) => {
      if (isFilterFormSelect(element)) {
        return;
      }
      observeSelect(element as SizedSelectElement);
    });
}

let setupComplete = false;

/** Initialise auto-width dropdown sizing across admin form grids and datatable filters. */
export function setupCbarSelectSizing(): void {
  if (setupComplete) {
    sizeFormSelects();
    return;
  }
  setupComplete = true;

  const refresh = () => {
    bindSelectObservers();
    sizeFormSelects();
  };

  refresh();

  if (typeof window.MutationObserver !== 'undefined') {
    const gridObserver = new MutationObserver(() => refresh());
    document.querySelectorAll('cbar-form-grid, cbar-datatable').forEach((container) => {
      gridObserver.observe(container, {childList: true, subtree: true});
    });
  }

  if (typeof window.jQuery !== 'undefined') {
    window.jQuery(document).ajaxComplete(() => refresh());
  }

  window.CBAR = window.CBAR || {};
  window.CBAR.sizeFormSelects = sizeFormSelects;
}

declare global {
  interface Window {
    CBAR?: Window['CBAR'] & {
      sizeFormSelects?: typeof sizeFormSelects;
    };
  }
}
